import React from "react";

const DeedPreviewRenderer = ({
  deedTitle = "DEED",
  previewItems,
  highlightedClauseId = "",
  clauseRefs = null,
  answersByInstanceId = {},
  previewGlobalAnswers = {},
  containerClassName = "deeds-preview-scroll",
}) => {
  const renderTextWithPlaceholders = (text, clauseInstanceId, lineKeyPrefix) => {
    if (!text) return null;
    const tokenRegex = /(\{\{[^}]+\}\}|\[[^\]]+\])/g;
    const parts = text.split(tokenRegex).filter((part) => part.length > 0);

    return parts.map((part, idx) => {
      const isPlaceholder = tokenRegex.test(part);
      tokenRegex.lastIndex = 0;

      if (!isPlaceholder) {
        return (
          <React.Fragment key={`${lineKeyPrefix}-t-${idx}`}>
            {part}
          </React.Fragment>
        );
      }

      const isSquare = part.startsWith("[") && part.endsWith("]");
      const normalized = part
        .replace(/^\{\{/, "")
        .replace(/\}\}$/, "")
        .replace(/^\[/, "")
        .replace(/\]$/, "")
        .trim();
      const isWide = /address|signature/i.test(normalized);

      if (isSquare && clauseInstanceId) {
        const fieldKey = normalized.split(".").slice(1).join(".");
        const filledValue =
          answersByInstanceId?.[clauseInstanceId]?.[fieldKey] ?? "";
        if (filledValue !== "") {
          return (
            <span
              key={`${lineKeyPrefix}-v-${idx}`}
              className="deed-filled"
              data-key={normalized}
            >
              {filledValue}
            </span>
          );
        }
      }

      if (isSquare) {
        const filledValue = previewGlobalAnswers?.[normalized] ?? "";
        if (filledValue !== "") {
          return (
            <span
              key={`${lineKeyPrefix}-g-${idx}`}
              className="deed-filled"
              data-key={normalized}
            >
              {filledValue}
            </span>
          );
        }
      }

      return (
        <span
          key={`${lineKeyPrefix}-b-${idx}`}
          className={`deed-blank${isWide ? " wide" : ""}`}
          data-key={normalized}
        />
      );
    });
  };

  return (
    <div className={containerClassName}>
      <div className="deeds-preview-title">{deedTitle}</div>
      <div className="deeds-preview-title-divider" />
      {Array.isArray(previewItems) &&
        previewItems.map((item, index) => {
          const isSection = item?.type === "section";
          if (isSection) {
            return (
              <div className="deeds-section" key={`section-${index}`}>
                <div className="deeds-section-title">{item?.title}</div>
              </div>
            );
          }

          const clauseId = `clause-${index}`;
          const text =
            typeof item?.text === "string" ? item.text.replace(/\\n/g, "\n") : "";
          const lines = text.split("\n");

          return (
            <div
              className={`deeds-clause ${
                highlightedClauseId === clauseId ? "highlighted" : ""
              }`}
              key={clauseId}
              ref={(el) => {
                if (clauseRefs?.current) {
                  clauseRefs.current[clauseId] = el;
                }
              }}
            >
              {(item?.title || item?.clause_number) && (
                <div className="deeds-clause-title">
                  {item?.clause_number?.toString().trim()
                    ? `${item.clause_number}. `
                    : ""}
                  {item?.title || ""}
                </div>
              )}
              <div className="deeds-clause-text">
                {lines.map((line, idx) => (
                  <React.Fragment key={`line-${idx}`}>
                    {renderTextWithPlaceholders(
                      line,
                      item?.party_instance_id || null,
                      `clause-${index}-line-${idx}`
                    )}
                    {idx < lines.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      {!Array.isArray(previewItems) && (
        <div className="deeds-muted">No preview content available.</div>
      )}
    </div>
  );
};

export default DeedPreviewRenderer;
